import { useCallback, useEffect, useMemo, useState } from 'react'
import type { SessionUser } from '@raporto/shared'
import { OfficeHead } from '../ui/OfficeShell.js'
import { notify, notifyError } from '../ui/Toasts.js'
import { BelegDetail } from './Documents.js'
import {
  api,
  type OffertFilter,
  type OffertenListe,
  type OffertZeile,
  type Sprung
} from '../net/apiClient.js'
import { uuidv7 } from '../net/ids.js'
import { PdfAnsicht } from '../ui/PdfAnsicht.js'
import { Datumsfeld } from '../ui/Datumsfeld.js'
import {
  useAuswahl,
  Auswahlkasten,
  AlleAufSeite,
  Auswahlleiste,
  Datumsleiste
} from '../ui/Belegauswahl.js'

/**
 * Die Offertenliste.
 *
 * Eine Offerte ist die einzige Sache in Raporto, bei der Nichtstun Geld
 * kostet: sie läuft ab. Deshalb steht die Gültigkeit in der Tabelle weit vorn,
 * und "läuft bald ab" ist ein eigener Hinweis und nicht ein Datum, das man
 * selbst vergleichen muss.
 *
 * Der Zustand wird gerechnet, nicht gespeichert – aus Belegzustand, der Antwort
 * des Kunden und dem Datum. "Abgelaufen" käme sonst erst dann an, wenn ein
 * nächtlicher Lauf es umlegt, und der Tag, an dem der ausfällt, ist der Tag,
 * an dem eine abgelaufene Offerte als offen gilt.
 */

const rp = (n: number): string =>
  (n / 100).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const datum = (iso: string | null): string =>
  iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('de-CH') : '–'

const ZUSTAND: Record<string, { text: string; klasse: string }> = {
  entwurf: { text: 'Entwurf', klasse: 'o-badge-neutral' },
  offen: { text: 'offen', klasse: 'o-badge-accent' },
  angenommen: { text: 'angenommen', klasse: 'o-badge-ok' },
  abgelehnt: { text: 'abgelehnt', klasse: 'o-badge-neutral' },
  abgelaufen: { text: 'abgelaufen', klasse: 'o-badge-alert' },
  storniert: { text: 'storniert', klasse: 'o-badge-neutral' }
}

const FILTER: Array<{ id: OffertFilter; label: string }> = [
  { id: 'offen', label: 'Offen' },
  { id: 'entwurf', label: 'Entwurf' },
  { id: 'angenommen', label: 'Angenommen' },
  { id: 'abgelehnt', label: 'Abgelehnt' },
  { id: 'abgelaufen', label: 'Abgelaufen' },
  { id: 'storniert', label: 'Storniert' },
  { id: 'alle', label: 'Alle' }
]

/** Wie viele Tage bleiben? Negativ heisst: vorbei. */
function tageBis(iso: string | null): number | null {
  if (!iso) return null
  const ziel = Date.parse(`${iso.slice(0, 10)}T00:00:00`)
  const heute = Date.parse(`${new Date().toISOString().slice(0, 10)}T00:00:00`)
  return Math.round((ziel - heute) / 86_400_000)
}

export function Quotes({
  session,
  aufBeleg,
  aufBereich,
  sprung,
  onSprungUebernommen,
  oeffneBeleg,
  onBelegGeoeffnet,
  aufUebersicht
}: {
  session: SessionUser
  aufBeleg?: (id: string) => void
  aufBereich?: (id: string) => void
  /** Eine vom Dashboard mitgegebene Einstellung – einmal übernehmen, dann melden. */
  sprung?: Sprung | null
  onSprungUebernommen?: () => void
  /**
   * Eine Offerte, die sofort geöffnet werden soll.
   *
   * Dieselbe Bauart wie bei den Rechnungen: Wer eine Offerte gerade erstellt
   * hat, kommt mit ihrer Kennung hierher und landet IN der Offerte, nicht in
   * einer Liste, in der er sie suchen muss.
   */
  oeffneBeleg?: string | null
  onBelegGeoeffnet?: () => void
  /** Der Weg zurück auf die Übersicht – nur sichtbar, wenn man von dort kam. */
  aufUebersicht?: () => void
}): JSX.Element {
  const [filter, setFilter] = useState<OffertFilter>('offen')
  /* Das PDF bleibt in der App – siehe ui/PdfAnsicht.tsx. */
  const [pdfAnsicht, setPdfAnsicht] = useState<{ url: string; titel: string } | null>(null)
  const [suche, setSuche] = useState('')
  /* Dieselbe Leiste wie bei den Rechnungen, mit den Datumsarten einer Offerte. */
  const [zeitraum, setZeitraum] = useState({ art: 'offerte', von: '', bis: '' })
  /* Sind wir über eine Kachel des Dashboards hier? Dann gibt es den Weg zurück. */
  const [vomDashboard, setVomDashboard] = useState(false)

  /* Dieselbe Übernahme wie bei den Rechnungen – einmal, dann zurückgemeldet. */
  useEffect(() => {
    if (!sprung) return
    if (sprung.filter) setFilter(sprung.filter as OffertFilter)
    setZeitraum(sprung.datum ?? { art: 'offerte', von: '', bis: '' })
    setSuche('')
    setVomDashboard(sprung.herkunft === 'dashboard')
    onSprungUebernommen?.()
  }, [sprung, onSprungUebernommen])
  const [liste, setListe] = useState<OffertenListe | null>(null)
  const [laedt, setLaedt] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)
  /* Die Offerte, für die gerade der Leistungszeitraum erfragt wird. */
  const [verrechnen, setVerrechnen] = useState<OffertZeile | null>(null)
  /*
    Die geöffnete Offerte.

    Sie bleibt IN diesem Bereich und wandert nicht in die Rechnungsliste:
    wer eine Offerte anschaut und zurückgeht, will zurück zu den Offerten.
    Ein Rücksprung in eine andere Liste ist der schnellste Weg, jemanden zu
    verlieren, der gerade drei Angebote durchsieht.
  */
  const [offen, setOffen] = useState<string | null>(null)

  const laden = useCallback(async () => {
    setLaedt(true)
    try {
      setListe(await api.quoteList(filter, suche, zeitraum))
    } catch (err) {
      notifyError(err)
    } finally {
      setLaedt(false)
    }
  }, [filter, suche, zeitraum])

  useEffect(() => {
    void laden()
  }, [laden])

  /*
    Eine mitgegebene Offerte wird direkt geöffnet.

    Ohne das endet «Zur Offerte» nach dem Abschliessen in einer Liste, und der
    Beleg, den man eben geschrieben hat, muss gesucht werden.
  */
  useEffect(() => {
    if (!oeffneBeleg) return
    setOffen(oeffneBeleg)
    onBelegGeoeffnet?.()
  }, [oeffneBeleg, onBelegGeoeffnet])

  /*
    ══════════════════════════════════════════════════════════════════════════
    Diese drei Haken stehen VOR dem Absprung in die Belegansicht – und zwar
    zwingend
    ══════════════════════════════════════════════════════════════════════════

    Sie standen darunter, hinter `if (offen) return <BelegDetail …>`, und das
    war der weisse Bildschirm der Offerten: React verlangt, dass eine
    Komponente bei jedem Durchlauf dieselbe Zahl Haken aufruft. In der Liste
    liefen drei Haken mehr als in der Belegansicht – und in dem Augenblick, in
    dem jemand eine Offerte öffnete, brach die Oberfläche mit «Minified React
    error #300» ab. Leere Seite, kein Hinweis, nichts mehr anklickbar.

    Gemessen am gebauten Stand: Klick auf `OFF-…` in der Offertenliste →
    `document.body` leer, `pageerror: Minified React error #300`.

    Derselbe Fehler stand in `screens/Documents.tsx` und wurde dort behoben;
    diese Datei war an der Übergabe nicht beteiligt und blieb zurück. Wer hier
    weiterarbeitet: Haken NIE hinter einen bedingten `return` setzen.

    In der Belegansicht kosten sie nichts: `liste` ist dieselbe, `useMemo`
    rechnet nicht neu, und ausgewertet wird das Ergebnis erst in der Liste.
  */
  /* Dieselbe Auswahl wie bei den Rechnungen – ein Bauteil, zwei Listen. */
  const sichtbar = useMemo(() => (liste?.items ?? []).map((q) => q.id), [liste])
  const auswahl = useAuswahl(sichtbar)
  const gewaehlteBelege = useMemo(
    () =>
      (liste?.items ?? [])
        .filter((q) => auswahl.istGewaehlt(q.id))
        .map((q) => ({
          id: q.id,
          nummer: q.number,
          status: q.status,
          hasPdf: q.hasPdf,
          bezeichnung: q.number ?? `Entwurf · ${q.customerName}`
        })),
    [liste, auswahl]
  )

  /*
    Keine Zwei-Faktor-Sperre auf «Rechnung erstellen».

    Hier stand `const gesperrt = !session.sensitiveAllowed`, und der Knopf war
    damit gesperrt, solange der zweite Faktor fehlte. Der Server verlangt für
    POST /quotes/:id/to-invoice aber nur `document.write` und nicht
    `requireSensitive` – aus gutem Grund: es entsteht ein ENTWURF, keine
    Nummer, kein Geld, kein Versand. Die Sperre war strenger als die Regel und
    endete in einer Sackgasse. Heikel wird es erst beim Abschliessen der
    Rechnung, und dort greift die Prüfung unverändert.
  */

  if (offen) {
    return (
      <BelegDetail
        session={session}
        id={offen}
        onBack={() => {
          setOffen(null)
          void laden()
        }}
      />
    )
  }

  async function antwort(
    q: OffertZeile,
    outcome: 'angenommen' | 'abgelehnt' | null
  ): Promise<void> {
    setBusy(q.id)
    try {
      await api.setQuoteOutcome(q.id, outcome, null, uuidv7())
      notify.ok(
        outcome === null
          ? 'Antwort zurückgenommen.'
          : `Offerte ${q.number} als ${outcome} vermerkt.`
      )
      /*
        Die Ansicht folgt der Antwort.

        Gemessen: Wer «Angenommen» drückte, las danach «Nichts in dieser
        Auswahl». Der Filter stand weiter auf «Offen» – und genau diese
        Offerte war soeben keine offene mehr. Der Zähler daneben sagte
        «Angenommen 1», die Liste zeigte nichts. Die eigene Offerte sah aus
        wie verschwunden, und der nächste Schritt – verrechnen – stand hinter
        einem Filterklick, auf den niemand kommt.

        Deshalb zieht der Filter mit, wenn die Antwort die Zeile aus der
        aktuellen Auswahl nehmen würde. Wer «Alle» eingestellt hat, bleibt
        dort: dann ist die Zeile ohnehin noch da, und ein Filterwechsel wäre
        eine Bevormundung.
      */
      const verschwindetHier =
        outcome !== null && (filter === 'offen' || filter === 'abgelaufen' || filter === 'entwurf')
      if (verschwindetHier) {
        /*
          Kein zusätzliches `laden()` hier: `laden` hängt am Filter, und der
          Effekt darüber lädt neu, sobald er sich ändert. Ein Aufruf an
          dieser Stelle liefe noch mit dem alten Filter und zeigte für einen
          Augenblick genau die leere Liste, die wir loswerden wollen.
        */
        setFilter(outcome)
      } else {
        await laden()
      }
    } catch (err) {
      notifyError(err)
    } finally {
      setBusy(null)
    }
  }

  /**
   * Rechnung aus der Offerte.
   *
   * Markiert sie gleich als angenommen: wer aus einer Offerte eine Rechnung
   * macht, hat die Zusage. Den Haken danach noch einmal separat zu verlangen,
   * wäre eine Frage, deren Antwort schon feststeht.
   */
  /**
   * Aus der Offerte eine Baustelle.
   *
   * Der Weg für alles, was nicht an einem Tag erledigt ist: erst die
   * Baustelle, dann Rapporte darauf, dann die Rechnung aus den Rapportzeilen.
   * Abgerechnet wird damit, was gearbeitet wurde – nicht, was geschätzt war.
   */
  async function zuProjekt(q: OffertZeile): Promise<void> {
    setBusy(q.id)
    try {
      const r = await api.quoteToProject(q.id, null, uuidv7())
      notify.ok(
        `Projekt «${r.name}» angelegt. Jetzt Mitarbeitende zuweisen – danach können ` +
          'sie Rapporte darauf erfassen.'
      )
      await laden()
    } catch (err) {
      notifyError(err)
    } finally {
      setBusy(null)
    }
  }

  async function zuRechnung(q: OffertZeile, leistung: { von: string; bis: string }): Promise<void> {
    setBusy(q.id)
    try {
      const antwort = await api.quoteToInvoice(q.id, true, leistung, uuidv7())
      notify.ok(
        `Rechnungsentwurf aus ${antwort.offertnummer} erstellt – ${antwort.lineCount} Positionen.`
      )
      await laden()
      aufBeleg?.(String(antwort.document.id))
    } catch (err) {
      notifyError(err)
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      {pdfAnsicht && (
        <PdfAnsicht
          url={pdfAnsicht.url}
          titel={pdfAnsicht.titel}
          aufZu={() => setPdfAnsicht(null)}
        />
      )}
      <OfficeHead
        titel="Offerten"
        unter={liste ? `${rp(liste.summe.offenRp)} CHF in offenen Angeboten` : 'Wird geladen …'}
      >
        {/* Der Weg zurück – nur, wenn man von der Übersicht kam. */}
        {vomDashboard && (
          <button
            type="button"
            className="o-btn o-btn-ghost"
            id="btn-zurueck-uebersicht"
            onClick={() => aufUebersicht?.()}
          >
            ← Übersicht
          </button>
        )}
        <button
          className="o-btn o-btn-primary"
          id="btn-neue-offerte"
          onClick={() => aufBereich?.('expressofferte')}
        >
          Neue Offerte
        </button>
      </OfficeHead>

      <div className="o-card">
        <div className="o-pad">
          <div className="o-filter" id="offertfilter">
            {FILTER.map((f) => (
              <button
                key={f.id}
                id={`ofilter-${f.id}`}
                className={f.id === filter ? 'o-chip aktiv' : 'o-chip'}
                onClick={() => setFilter(f.id)}
              >
                {f.label}
                <span className="o-chip-zahl">{liste?.zaehler[f.id] ?? 0}</span>
              </button>
            ))}
          </div>
          <div className="o-field" style={{ marginTop: 16, maxWidth: 420 }}>
            <label htmlFor="offert-suche">Suche</label>
            <input
              id="offert-suche"
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
              placeholder="Offertnummer, Kunde oder Projekt"
              autoComplete="off"
            />
          </div>
          <Datumsleiste
            kennung="offert"
            arten={[
              { id: 'offerte', label: 'Offertdatum' },
              { id: 'gueltig', label: 'Gültig bis' },
              { id: 'erstellt', label: 'Erstellt' }
            ]}
            wert={zeitraum}
            aufWert={setZeitraum}
          />
        </div>
      </div>

      <div className="o-card" id="offertenliste">
        <div className="o-card-head">
          <h3>
            {FILTER.find((f) => f.id === filter)?.label}{' '}
            <span className="o-badge o-badge-accent">{liste?.items.length ?? 0}</span>
          </h3>
          <span className="o-soft">
            Zustand aus Antwort und Gültigkeit gerechnet, nicht gespeichert.
          </span>
        </div>

        {laedt ? (
          <div className="o-pad">
            <p className="o-muted" style={{ margin: 0 }}>
              Wird geladen …
            </p>
          </div>
        ) : (liste?.items.length ?? 0) === 0 ? (
          <div className="o-pad">
            <p className="o-muted" style={{ margin: 0 }}>
              Nichts in dieser Auswahl.
            </p>
          </div>
        ) : (
          /*
            Der Scrollkasten um die Tabelle – er fehlte, und das war der ganze
            Fehler.

            Eine `table` mit `width: 100%` und acht festen Spaltenbreiten wächst
            über die 100 % hinaus, sobald ihr Mindestinhalt breiter ist als der
            Platz. Ohne Kasten mit `overflow-x` läuft sie einfach aus der Karte
            heraus: bei 1440 px ragten die Aktionsknöpfe rund 330 px über den
            rechten Kartenrand hinaus und lagen über dem, was daneben stand.
            Andere Listen im Büro haben diesen Kasten; die Offertenliste hatte
            ihn als einzige nicht.
          */
          <>
            {/*
              «Alle auf dieser Seite» steht über der Tabelle und nicht in ihrer
              Kopfzeile – so wie bei den Rechnungen.

              In der Kopfzeile stand es zuerst, und dort war es am Telefon weg:
              Dort wird aus jeder Zeile eine Karte und die Kopfzeile
              ausgeblendet, samt dem Kästchen darin. Über der Tabelle bleibt es
              in jeder Breite da, und beide Belegbildschirme tragen dasselbe
              Bedienelement an derselben Stelle.
            */}
            <div className="o-wahlzeile">
              <AlleAufSeite stand={auswahl} anzahl={liste?.items.length ?? 0} />
            </div>
            <div className="o-tabelle">
              <table className="o-data">
                <thead>
                  <tr>
                    <th className="o-spalte-wahl" aria-label="Auswahl" />
                    <th style={{ width: 130 }}>Nummer</th>
                    <th className="o-langtext">Kunde</th>
                    <th className="o-langtext o-spalte-baustelle">Projekt</th>
                    <th className="o-spalte-datum" style={{ width: 100 }}>
                      Datum
                    </th>
                    <th className="o-spalte-gueltig" style={{ width: 112 }}>
                      Gültig bis
                    </th>
                    <th className="o-num o-spalte-total" style={{ width: 115 }}>
                      Total
                    </th>
                    <th style={{ width: 104 }}>Zustand</th>
                    {/*
                  Die Spalte «Nächster Schritt» steht hier nicht mehr.

                  Sie zeigt die Aufgabe, die beim Abschliessen einer RECHNUNG
                  mitgegeben werden kann («Zahlungseingang prüfen»). An einer
                  Offerte gibt es das nicht – in dieser Tabelle stand in jeder
                  Zeile ein «–». Die 150 px, die sie belegte, sind genau die,
                  die den drei Aktionsknöpfen fehlten; ohne sie passen sie bei
                  1440 px nebeneinander, statt auf drei Zeilen umzubrechen.
                */}
                    <th className="o-aktionsspalte" />
                  </tr>
                </thead>
                <tbody>
                  {liste?.items.map((q) => {
                    const tage = tageBis(q.validUntil)
                    return (
                      <tr
                        key={q.id}
                        id={`offerte-${q.id}`}
                        className="klickbar"
                        onClick={() => setOffen(q.id)}
                      >
                        {/*
                        `data-feld` ist nicht Zierde: Am Telefon wird aus jeder
                        Tabellenzeile eine Karte, und eine Zelle ohne diesen
                        Namen gilt dort als Aktionszelle – rechtsbündig, ohne
                        Beschriftung. Das Kästchen sass dadurch allein am
                        rechten Rand der Karte und sah aus wie ein Knopf. Mit
                        dem Namen steht es als erste Zeile der Karte, links,
                        mit «Auswählen» davor.
                      */}
                        <td className="o-spalte-wahl" data-feld="Auswählen">
                          <Auswahlkasten
                            id={q.id}
                            stand={auswahl}
                            titel={`${q.number ?? 'Entwurf'} auswählen`}
                          />
                        </td>
                        <td className="o-mono">
                          {q.number ?? <span className="o-badge o-badge-neutral">Entwurf</span>}
                        </td>
                        <td className="o-langtext">{q.customerName}</td>
                        <td className="o-soft o-langtext o-spalte-baustelle">
                          {q.projectName ?? '–'}
                        </td>
                        <td className="o-spalte-datum">{datum(q.issueDate)}</td>
                        <td className="o-spalte-gueltig">
                          {datum(q.validUntil)}
                          {/*
                        Die Restlaufzeit als Wort statt als Datum zum
                        Nachrechnen. "noch 3 Tage" ist eine Aufforderung,
                        "16.9.2026" ist eine Angabe.
                      */}
                          {q.zustand === 'offen' && tage !== null && tage <= 8 && (
                            <div>
                              <span className="o-badge o-badge-warn">
                                {tage <= 0 ? 'läuft heute ab' : `noch ${tage} Tage`}
                              </span>
                            </div>
                          )}
                        </td>
                        <td className="o-num o-spalte-total">{rp(q.totalRp)}</td>
                        <td>
                          <span
                            className={`o-badge ${ZUSTAND[q.zustand]?.klasse ?? ''}`}
                            id={`ozustand-${q.id}`}
                          >
                            {ZUSTAND[q.zustand]?.text ?? q.zustand}
                          </span>
                          {q.rechnungen > 0 && (
                            <div className="o-soft">
                              {q.rechnungen} Rechnung{q.rechnungen > 1 ? 'en' : ''}
                            </div>
                          )}
                        </td>
                        <td className="o-aktionen">
                          <div className="office-actions" onClick={(e) => e.stopPropagation()}>
                            {q.zustand === 'offen' || q.zustand === 'abgelaufen' ? (
                              <>
                                <button
                                  className="o-btn"
                                  id={`annehmen-${q.id}`}
                                  disabled={busy !== null}
                                  onClick={() => void antwort(q, 'angenommen')}
                                >
                                  Angenommen
                                </button>
                                <button
                                  className="o-btn o-btn-ghost"
                                  id={`ablehnen-${q.id}`}
                                  disabled={busy !== null}
                                  onClick={() => void antwort(q, 'abgelehnt')}
                                >
                                  Abgelehnt
                                </button>
                              </>
                            ) : q.zustand === 'angenommen' ? (
                              <>
                                <button
                                  className="o-btn o-btn-primary"
                                  id={`zurechnung-${q.id}`}
                                  disabled={busy !== null}
                                  title={
                                    q.rechnungen > 0
                                      ? 'Aus dieser Offerte nochmal eine Rechnung erstellen'
                                      : 'Aus dieser Offerte eine Rechnung erstellen'
                                  }
                                  onClick={() => setVerrechnen(q)}
                                >
                                  {q.rechnungen > 0 ? 'Nochmal' : 'Verrechnen'}
                                </button>
                                {/*
                              Der zweite Weg, und für längere Aufträge der
                              richtige: erst ein Projekt, dann Rapporte
                              darauf, dann die Rechnung aus den Rapportzeilen.
                            */}
                                <button
                                  className="o-btn"
                                  id={`zuprojekt-${q.id}`}
                                  disabled={busy !== null}
                                  title="Projekt anlegen – erst Projekt, dann Rapporte, dann Rechnung"
                                  onClick={() => void zuProjekt(q)}
                                >
                                  Baustelle
                                </button>
                              </>
                            ) : null}
                            {q.number && q.hasPdf && (
                              <button
                                type="button"
                                className="o-btn o-btn-ghost"
                                id={`opdf-${q.id}`}
                                onClick={() =>
                                  setPdfAnsicht({
                                    url: `/documents/${q.id}/pdf`,
                                    titel: q.number ?? 'Offerte'
                                  })
                                }
                              >
                                PDF
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/*
        Die Auswahlleiste – dieselbe wie bei den Rechnungen.

        Sie fehlte hier, und das war kein Schönheitsfehler: Die Kästchen in der
        Tabelle liessen sich anhaken, der Zähler erschien nicht, und der
        Sammeldownload war auf diesem Bildschirm überhaupt nicht erreichbar.
        Aufgefallen ist es erst beim Nachmessen am Telefon – angehakt, nichts
        passiert. Die Prüfung, die das durchliess, sah nach dem `import` und
        nicht nach dem, was auf dem Schirm steht; sie prüft jetzt beides.
      */}
      <Auswahlleiste
        stand={auswahl}
        belege={gewaehlteBelege}
        was="Offerte"
        aufGetan={() => void laden()}
      />

      {verrechnen && (
        <LeistungsdauerFrage
          offerte={verrechnen}
          busy={busy === verrechnen.id}
          aufAbbruch={() => setVerrechnen(null)}
          aufWeiter={async (leistung) => {
            const q = verrechnen
            setVerrechnen(null)
            await zuRechnung(q, leistung)
          }}
        />
      )}
    </>
  )
}

/**
 * Der Leistungszeitraum, bevor aus der Offerte eine Rechnung wird.
 *
 * ---------------------------------------------------------------------------
 * Warum das eine eigene Frage ist und kein Feld im Formular
 * ---------------------------------------------------------------------------
 *
 * Eine Offerte trägt kein Leistungsdatum – sie beschreibt Arbeit, die noch
 * nicht stattgefunden hat. Eine Rechnung braucht eines: es steht auf dem
 * Beleg, und nach ihm richtet sich die Mehrwertsteuerperiode. Der einzige
 * Moment, in dem jemand die Antwort sicher weiss, ist der Moment der
 * Umwandlung – danach ist die Rechnung geschrieben, und ein Feld, das man
 * nachtragen könnte, trägt niemand nach.
 *
 * Deshalb steht die Frage hier und nicht als Pflichtfeld beim Abschliessen.
 * Der Server verlangt sie ebenfalls (routes/quotes.ts); diese Maske ist die
 * freundliche Seite derselben Regel.
 *
 * Vorbelegt ist beides mit heute – der häufigste Fall ist «heute fertig
 * geworden», und ein leeres Pflichtfeld ist eine Aufgabe, ein vorbelegtes eine
 * Bestätigung.
 */
function LeistungsdauerFrage({
  offerte,
  busy,
  aufWeiter,
  aufAbbruch
}: {
  offerte: OffertZeile
  busy: boolean
  aufWeiter: (leistung: { von: string; bis: string }) => void | Promise<void>
  aufAbbruch: () => void
}): JSX.Element {
  const heute = new Date().toISOString().slice(0, 10)
  const [von, setVon] = useState(heute)
  const [bis, setBis] = useState(heute)
  const verdreht = von > bis

  return (
    <div className="o-dialog-grund" id="leistungsdauer-frage">
      <div className="o-dialog">
        <h3>Leistungszeitraum</h3>
        <p className="o-soft">
          Von wann bis wann wurde für {offerte.customerName} gearbeitet? Die Angabe steht auf der
          Rechnung; nach ihr richtet sich die Mehrwertsteuerperiode. Ohne sie lässt sich aus der
          Offerte {offerte.number ?? ''} keine Rechnung machen.
        </p>

        <div className="o-dialog-felder">
          {/* Dasselbe Datumsfeld wie überall sonst im Büro – ein Klick ins Feld
              öffnet den Kalender. Siehe ui/Datumsfeld.tsx. */}
          <label htmlFor="leistung-von">
            Von
            <Datumsfeld id="leistung-von" value={von} max={bis} onChange={setVon} />
          </label>
          <label htmlFor="leistung-bis">
            Bis
            <Datumsfeld id="leistung-bis" value={bis} min={von} onChange={setBis} />
          </label>
        </div>

        {verdreht && (
          <p className="o-soft" role="alert" id="leistung-verdreht">
            Das Ende darf nicht vor dem Beginn liegen.
          </p>
        )}
        <p className="o-soft">Ein einzelner Tag: beide Felder gleich lassen.</p>

        <div className="office-actions">
          <button
            type="button"
            className="o-btn o-btn-primary"
            id="btn-leistung-weiter"
            disabled={busy || verdreht || !von || !bis}
            onClick={() => void aufWeiter({ von, bis })}
          >
            {busy ? 'Erstellt …' : 'Rechnungsentwurf erstellen'}
          </button>
          <button
            type="button"
            className="o-btn o-btn-ghost"
            id="btn-leistung-abbruch"
            disabled={busy}
            onClick={aufAbbruch}
          >
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  )
}
